import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import { useUserStore } from '../../store/useUserStore';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import toast from 'react-hot-toast';
import { GENDER, ANALYSIS_STATUS } from '../../data/define';
import { useRemoveKidMutation } from '../../queries';
import useUpdateUser from '../../hook/useUpdateUser.js';
import IcKidFace from '../../assets/image/img_kidface.png';
import IcPlus from '../../assets/image/ic_plus_gray_circle.svg';
import BottomTwoBtnDialog from '../common/BottomTwoBtnDialog';
import TwoBtns from '../common/TwoBtns';
import BirthDatePickerDialog from '../../component/register/BirthDatePickerDialog';
import TransparentTwoBtnDialog from '../common/TransparentTwoBtnDialog';
import { useTranslation } from '../../hook/useTranslation';

function KidProfileEdit({
  kid,
  profileFile = null,
  setProfileFile,
  name,
  setName,
  birthInput,
  setBirthInput,
  datePickerOpen,
  setDatePickerOpen,
  gender,
  setGender,
}: any) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updateUser } = useUpdateUser();
  const user = useUserStore();
  const analysisResult = useAnalysisStore((s) => s.analysisResult);
  const fileRef = useRef<HTMLInputElement>();
  const [imageBtnDialogOpen, setImageBtnDialogOpen] = useState(false);
  const [focusField, setFocusField] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleAddImage = useCallback(() => {
    (fileRef.current as any)?.click();
  }, [fileRef]);

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증 (png, jpg, jpeg만 허용)
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('my.profile.toast_img_type_error'));
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('my.profile.toast_img_size_error'));
      e.target.value = '';
      return;
    }

    setProfileFile({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    });
    e.target.value = '';
  };

  const { mutate: removeKid } = useRemoveKidMutation(user.id || 0);

  const handleDelete = useCallback(async () => {
    if (!kid) return;
    removeKid(kid.id, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        updateUser();
        navigate(-1);
      },
      onError: (err) => {
        toast.error(t('my.profile.toast_delete_fail', '아이 정보를 삭제하는데 실패했어요.'));
        console.error('Error deleting kid:', err);
      },
    });
  }, [kid, navigate, updateUser, removeKid, t]);

  return (
    <div className="w-full flex flex-col items-center justify-between h-full py-[24px] rounded-[24px]">
      <div className="w-full flex flex-col justify-start items-center">
        <div className="relative bg-white flex items-center justify-center mb-[16px]">
          <input
            type="file"
            ref={fileRef as any}
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {profileFile && profileFile?.url ? (
            <img
              src={profileFile?.url}
              alt="thumnail1"
              className="w-[100px] h-[100px] border overflow-hidden rounded-[40px] border-[#EAEAEA]"
            />
          ) : (
            <div className="w-[100px] h-[100px] flex items-center justify-center border overflow-hidden rounded-[40px] bg-[#F0FCFC] border-[#EAEAEA]">
              <img
                src={IcKidFace}
                alt="thumnail2"
                className="w-[56px] h-[56px] border overflow-hidden rounded-[50%] border-[#EAEAEA]"
              />
            </div>
          )}
          <button
            type="button"
            className="absolute bottom-[12px] right-[12px] translate-x-[50%] translate-y-[50%]"
            onClick={() => setImageBtnDialogOpen(true)}
            style={{ zIndex: 1 }}
          >
            <img src={IcPlus} alt="IcPlus" />
          </button>
          <TransparentTwoBtnDialog
            open={imageBtnDialogOpen}
            onClose={() => setImageBtnDialogOpen(false)}
            leftBtnTitle={t('my.profile.select_photo')}
            rightBtnTitle={t('common.close')}
            onLeftClick={() => {
              setImageBtnDialogOpen(false);
              handleAddImage();
            }}
            onRightClick={() => {
              setImageBtnDialogOpen(false);
            }}
          />
        </div>
        <div className="w-full text-[12px] text-[#7D7D7D] mb-[28px] flex flex-col items-start justify-start gap-2 mt-[24px]">
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            label={t('my.profile.name')}
            variant="standard"
            fullWidth
          />
          <div
            className="font-[400] text-[12px] text-[#363636] mt-[28px]"
            style={{ color: focusField === 'birth' ? '#5EBEBB' : '#363636' }}
          >
            {t('my.profile.birth')}
          </div>
          <TextField
            variant="standard"
            value={birthInput ?? ''}
            fullWidth
            placeholder="0000-00-00"
            onClick={() => setDatePickerOpen(true)}
            onFocus={() => {
              setFocusField('birth');
            }}
            onBlur={() => setFocusField(null)}
          />
          <BirthDatePickerDialog
            open={datePickerOpen}
            onClose={() => {
              setDatePickerOpen(false);
            }}
            onConfirm={() => {
              setDatePickerOpen(false);
            }}
            birthInput={birthInput}
            setBirthInput={setBirthInput}
          />
        </div>
        <div className="font-[400] text-[12px] text-[#363636] mb-[10px] w-full text-left">{t('my.profile.gender')}</div>
        <div className="w-full mb-[40px]">
          <TwoBtns
            leftTitle={t('register.male')}
            rightTitle={t('register.female')}
            onLeftClick={() => setGender(GENDER.MALE)}
            onRightClick={() => setGender(GENDER.FEMALE)}
            leftFocus={gender && gender === GENDER.MALE}
            rightFocus={gender && gender === GENDER.FEMALE}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className="underline font-[400] text-[12px] text-[#A2A2A2]"
          onClick={() => {
            if (
              user?.mainKid?.id === kid?.id &&
              analysisResult?.status === ANALYSIS_STATUS.PENDING
            ) {
              toast.error(t('my.profile.toast_delete_analysing'));
            } else {
              setDeleteDialogOpen(true);
            }
          }}
        >
          {t('my.profile.delete_kid')}
        </button>
        <BottomTwoBtnDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title={t('my.profile.delete_alert_title')}
          description={t('my.profile.delete_alert_desc')}
          leftBtnText={t('common.cancel')}
          rightBtnText={t('common.delete')}
          leftBtnClick={() => {
            setDeleteDialogOpen(false);
          }}
          rightBtnClick={() => {
            handleDelete();
          }}
        />
      </div>
    </div>
  );
}

export default KidProfileEdit;
